import { CLOUD, loggedIn, sbRest, sbUrl } from './api.js';
import { t } from '../i18n/index.js';
import { S, defaultState, rawSave, setState } from '../state.js';
import { applyTheme } from '../theme.js';
import { render, sheetStack } from '../ui.js';
import { updateAccountUI } from './account.js';

/* Cloud is the source of truth, one row per record.
   Load: one RPC (plume_load). Push: diff S against the last synced snapshot,
   upsert changed records / delete removed ones per table. Poll: one timestamp
   (organizations.updated_at) — any change anywhere bumps it. */

export function syncLoadLS(){
  try{CLOUD.rev=localStorage.getItem('plume.rev')||null}catch(e){}
  try{CLOUD.dirty=localStorage.getItem('plume.dirty')==='1'}catch(e){}
  /* last known workspace context (role, plan, school …) of this person: the first screen after a restart
     already looks right (school overview, plan badge) instead of flashing the personal view until the cloud answers */
  try{const c=JSON.parse(localStorage.getItem('plume.ctx')||'null');
    if(c&&c.ctx&&CLOUD.auth&&c.uid===CLOUD.auth.uid&&!CLOUD.ctx)CLOUD.ctx=c.ctx}catch(e){}
}
export function syncPersistLS(){
  try{if(CLOUD.rev)localStorage.setItem('plume.rev',CLOUD.rev);else localStorage.removeItem('plume.rev');
      if(CLOUD.dirty)localStorage.setItem('plume.dirty','1');else localStorage.removeItem('plume.dirty')}catch(e){}
}
export const sameRev=(a,b)=>!!a&&!!b&&String(a)===String(b);

/* ---------------- snapshot of what the cloud currently holds ---------------- */
const snap={students:{},recs:{},sessions:{},invoices:{},settings:'',prefs:''};
const idx=arr=>{const m={};for(const o of arr||[])if(o&&o.id)m[o.id]=JSON.stringify(o);return m};
const PREF_KEYS=['lang','theme','rail','notify','avail'];
function splitSettings(){
  const st=S.settings||{};const doc={},prefs={};
  for(const k in st)(PREF_KEYS.includes(k)?prefs:doc)[k]=st[k];
  prefs.dismissed=st.dismissed||{};
  return{doc,prefs,templates:S.templates||{}};
}
function takeSnapshot(){
  snap.students=idx(S.students);snap.recs=idx(S.recs);snap.sessions=idx(S.sessions);snap.invoices=idx(S.invoices);
  const sp=splitSettings();snap.settings=JSON.stringify([sp.doc,sp.templates]);snap.prefs=JSON.stringify(sp.prefs);
}
function clearSnapshot(){snap.students={};snap.recs={};snap.sessions={};snap.invoices={};snap.settings='';snap.prefs=''}

/* ---------------- record → row ---------------- */
const nn=v=>(v===''||v===undefined)?null:v;
const tutorOf=stuId=>{const s=(S.students||[]).find(x=>x.id===stuId);return s&&('tutorId' in s)?s.tutorId:CLOUD.auth.uid};
/* records from a school a teacher works for carry _org; they're saved to that school */
const orgOf=stuId=>{const s=(S.students||[]).find(x=>x.id===stuId);return (s&&s._org)||CLOUD.org};
const clean=o=>{const c={};for(const k in o)if(k[0]!=='_')c[k]=o[k];return c};
const ROW={
  students:s=>({id:s.id,org_id:s._org||CLOUD.org,tutor_id:('tutorId' in s)?s.tutorId:CLOUD.auth.uid,
    first_name:s.first||'?',last_name:nn(s.last),email:nn(s.email),phone:nn(s.phone),grade:nn(s.level),
    subject:nn(s.subject),rate:nn(s.rate),archived:!!s.archived,notes:nn(s.notes),
    meeting:nn(s.meet),goals:nn(s.goals),color:nn(s.color),contact_lang:nn(s.contactLang),doc:clean(s)}),
  recs:r=>({id:r.id,org_id:r._org||orgOf(r.studentId),student_id:r.studentId,tutor_id:tutorOf(r.studentId),
    freq:nn(r.freq),days:r.days||null,start_date:nn(r.start),until_date:nn(r.until),
    start_time:nn(r.time),minutes:nn(+r.dur||null),ended:!!r.ended,doc:clean(r)}),
  sessions:e=>({id:e.id,org_id:e._org||orgOf(e.studentId),student_id:e.studentId,tutor_id:tutorOf(e.studentId),
    lesson_date:e.date,start_time:nn(e.time),minutes:+e.dur||60,
    status:['planned','done','noshow','cancelled'].includes(e.status)?e.status:'planned',
    billable:e.billable!==false,rate_override:nn(e.rate),subject:nn(e.subject),invoice_id:nn(e.invoiceId),
    recap:e.summary||null,notes:nn(e.notes),meeting_link:nn(e.meet),recurrence_id:nn(e.recId),doc:clean(e)}),
  invoices:i=>({id:i.id,org_id:CLOUD.org,student_id:nn(i.studentId),number:i.no||i.id,
    issue_date:i.issue,due_date:nn(i.due),period_label:nn(i.period),subtotal:nn(i.subtotal),
    vat_amount:nn(i.vat),total:+i.total||0,
    status:['draft','sent','paid','overdue','cancelled'].includes(i.status)?i.status:'draft',
    paid_on:nn(i.paidOn),payment_method:nn(i.method),
    reminder_sent_at:i.remindedOn?new Date(i.remindedOn).toISOString():null,doc:i}),
};
const TABLE={students:'students',recs:'recurrences',sessions:'lessons',invoices:'invoices'};
const PUSH_ORDER=['students','recs','invoices','sessions'];   // parents before children
const DEL_ORDER=['sessions','recs','invoices','students'];

async function upsert(table,rows){
  if(!rows.length)return;
  await sbRest(table+'?on_conflict=id',{method:'POST',
    headers:{Prefer:'resolution=merge-duplicates,return=minimal'},body:JSON.stringify(rows)});
}
async function remove(table,ids,orgFor){
  if(!ids.length)return;
  const by={};for(const i of ids){const o=(orgFor&&orgFor(i))||CLOUD.org;(by[o]=by[o]||[]).push(i)}
  for(const o in by){
    const list=by[o].map(i=>'"'+String(i).replace(/"/g,'')+'"').join(',');
    await sbRest(table+'?org_id=eq.'+o+'&id=in.('+list+')',{method:'DELETE',headers:{Prefer:'return=minimal'}});
  }
}
async function fetchRev(){
  const ids=[CLOUD.org,...(CLOUD.schoolOrgs||[])].filter(Boolean);
  const r=await sbRest('organizations?select=id,updated_at&id=in.('+ids.join(',')+')');
  const rows=await r.json();if(!rows||!rows.length)return null;
  return rows.map(x=>x.id+'='+new Date(x.updated_at).getTime()).sort().join(',');
}

/* ---------------- push: only what changed ---------------- */
export async function cloudPush(fetchOpts){
  if(!loggedIn()||!CLOUD.org)return;
  if(CLOUD.pushing){CLOUD.dirty=true;syncPersistLS();return}
  CLOUD.pushing=true;setStatus('saving');let ok=false;
  try{
    const cur={students:idx(S.students),recs:idx(S.recs),sessions:idx(S.sessions),invoices:idx(S.invoices)};
    const finished=[];   // school lessons that just became 'done': the director hears about them
    for(const id in cur.sessions){if(cur.sessions[id]===snap.sessions[id])continue;
      const e=JSON.parse(cur.sessions[id]);let was=null;try{was=snap.sessions[id]?JSON.parse(snap.sessions[id]).status:null}catch(x){}
      if(e.status==='done'&&was!=='done'&&(e._org||orgOf(e.studentId)!==CLOUD.org||(CLOUD.ctx&&CLOUD.ctx.role==='tutor')))finished.push(id)}
    let parentMail=false;   // a parent email changed: the parent page invitations follow
    for(const id in cur.students){if(cur.students[id]===snap.students[id])continue;
      try{const a=JSON.parse(cur.students[id]),b=snap.students[id]?JSON.parse(snap.students[id]):{};
        if((a.parentEmail||'')!==(b.parentEmail||'')||(a.parentEmail2||'')!==(b.parentEmail2||'')||!!a.archived!==!!b.archived)parentMail=true}catch(x){}}
    for(const k of PUSH_ORDER){
      const rows=[];
      for(const id in cur[k])if(cur[k][id]!==snap[k][id])rows.push(ROW[k](JSON.parse(cur[k][id])));
      await upsert(TABLE[k],rows);
    }
    for(const k of DEL_ORDER){
      const ids=[];for(const id in snap[k])if(!(id in cur[k]))ids.push(id);
      await remove(TABLE[k],ids,id=>{try{const o=JSON.parse(snap[k][id]);return o._org||(o.studentId?orgOf(o.studentId):null)}catch(e){return null}});
    }
    const sp=splitSettings();
    if(JSON.stringify([sp.doc,sp.templates])!==snap.settings)
      await sbRest('org_settings?org_id=eq.'+CLOUD.org,{method:'PATCH',headers:{Prefer:'return=minimal'},
        body:JSON.stringify({doc:sp.doc,templates:sp.templates,
          display_name:nn(sp.doc.teacher),business_name:nn(sp.doc.biz),address:nn(sp.doc.addr),tax_number:nn(sp.doc.taxId),
          iban:nn(sp.doc.iban),bank:nn(sp.doc.bank),paypal:nn(sp.doc.paypal),default_rate:nn(sp.doc.defRate),
          default_minutes:nn(sp.doc.defDur),currency:nn(sp.doc.currency),invoice_prefix:nn(sp.doc.invPrefix),
          next_invoice_no:nn(sp.doc.invNext),vat_enabled:!!sp.doc.vatOn,vat_pct:nn(sp.doc.vatP),
          klein_note:!!sp.doc.kleinOn,due_days:nn(sp.doc.dueDays)})});
    if(JSON.stringify(sp.prefs)!==snap.prefs)
      await sbRest('user_prefs?user_id=eq.'+CLOUD.auth.uid,{method:'PATCH',headers:{Prefer:'return=minimal'},
        body:JSON.stringify({ui_lang:sp.prefs.lang||'de',theme:sp.prefs.theme||'auto',doc:{rail:sp.prefs.rail||null,notify:sp.prefs.notify||null,dismissed:sp.prefs.dismissed||{},avail:sp.prefs.avail||[]}})});
    takeSnapshot();
    CLOUD.rev=await fetchRev();
    CLOUD.dirty=false;syncPersistLS();setStatus('synced');ok=true;
    if(parentMail)try{window.dispatchEvent(new CustomEvent('plume:parents'))}catch(x){}
    if(finished.length)fetch(sbUrl('/functions/v1/notify/done'),{method:'POST',headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,'Content-Type':'application/json'},body:JSON.stringify({lessons:finished.slice(0,20)})}).catch(()=>{});
  }catch(e){CLOUD.dirty=true;CLOUD.lastErr=String(e&&e.message||e);syncPersistLS();setStatus(navigator.onLine===false?'offline':'error');console.error('push',e)}
  finally{
    CLOUD.pushing=false;
    if(CLOUD.dirty&&navigator.onLine!==false){clearTimeout(CLOUD.t);CLOUD.t=setTimeout(()=>cloudPush().catch(()=>{}),ok?200:5000)}
  }
}
export function queueCloudSave(){
  if(!loggedIn()||CLOUD.muted)return;
  CLOUD.dirty=true;syncPersistLS();
  clearTimeout(CLOUD.t);setStatus('saving');
  CLOUD.t=setTimeout(async()=>{try{await cloudPush()}catch(e){}},800);
}
window.queueCloudSave=queueCloudSave;

export function setStatus(st){
  CLOUD.status=st;
  const label={off:t('cloud.local'),synced:t('cloud.synced'),saving:t('cloud.saving'),error:t('cloud.err'),offline:t('cloud.offlineQ')}[st]||'';
  if(st==='synced')CLOUD.lastErr='';
  document.querySelectorAll('[data-sync]').forEach(el=>{el.textContent=label;el.className='syncbadge '+(st==='offline'?'saving':st);el.title=st==='error'&&CLOUD.lastErr?CLOUD.lastErr:''});
  document.querySelectorAll('[data-syncerr]').forEach(el=>{el.hidden=st!=='error';const m=el.querySelector('.se-msg');if(m)m.textContent=CLOUD.lastErr||''});
}

/* ---------------- load ---------------- */
async function rpc(name,body){const r=await sbRest('rpc/'+name,{method:'POST',body:JSON.stringify(body||{})});try{return await r.json()}catch(e){return null}}
async function cloudLoad(){
  let p=await rpc('plume_load');
  if(!p||!p.ctx){await rpc('ensure_org');p=await rpc('plume_load')}
  if(!p||!p.ctx)throw new Error('no org');
  /* a teacher's home is their own workspace; the schools they teach for are folded into it */
  if(p.ctx.role==='tutor'){
    const orgs=await rpc('my_orgs').catch(()=>null);const own=(orgs||[]).find(o=>o.role==='owner');
    if(own){await rpc('switch_org',{p_org:own.org_id}).catch(()=>{});const q=await rpc('plume_load');if(q&&q.ctx)p=q}
  }
  p.schools=p.ctx.role==='tutor'?[]:((await rpc('school_load').catch(()=>null))||[]);
  if(!Array.isArray(p.schools))p.schools=[];
  CLOUD.org=p.ctx.org_id;CLOUD.schoolOrgs=p.schools.map(x=>x.org_id);
  CLOUD.schools=p.schools.map(x=>({org_id:x.org_id,org_name:x.org_name||'',terms:x.terms||{},invoices:x.invoices||[]}));
  p.rev=(await fetchRev().catch(()=>null))||p.rev;
  return p;
}
const hasData=o=>!!o&&(((o.students||[]).length+(o.sessions||[]).length+(o.invoices||[]).length)>0);

export function adoptCloud(p){
  if(!p||!p.ctx){setStatus('error');return false}
  CLOUD.ctx=p.ctx;CLOUD.org=p.ctx.org_id;
  try{localStorage.setItem('plume.owner',CLOUD.auth.uid);localStorage.setItem('plume.ctx',JSON.stringify({uid:CLOUD.auth.uid,ctx:p.ctx}))}catch(e){}
  const base=defaultState();
  const doc=(p.settings&&p.settings.doc)||{};const pr=p.prefs||{};
  const settings={...base.settings,...doc,lang:pr.lang||base.settings.lang,theme:pr.theme||'auto'};
  if(pr.doc&&pr.doc.rail)settings.rail=pr.doc.rail;
  if(pr.doc&&pr.doc.notify)settings.notify=pr.doc.notify;
  if(pr.doc&&Array.isArray(pr.doc.avail))settings.avail=pr.doc.avail;
  if(pr.doc&&pr.doc.dismissed)settings.dismissed={...(settings.dismissed||{}),...pr.doc.dismissed};
  if(!settings.dismissed)settings.dismissed={};
  const next={v:1,settings,templates:(p.settings&&p.settings.templates)||{},
    students:p.students||[],recs:p.recs||[],sessions:p.sessions||[],invoices:p.invoices||[]};
  for(const sc of p.schools||[]){const tag=o=>({...o,_org:sc.org_id,_school:sc.org_name||''});
    next.students.push(...(sc.students||[]).map(tag));next.recs.push(...(sc.recs||[]).map(tag));next.sessions.push(...(sc.sessions||[]).map(tag))}
  CLOUD.muted=true;
  try{setState(next);rawSave(S)}finally{CLOUD.muted=false}
  takeSnapshot();
  CLOUD.rev=p.rev||CLOUD.rev;CLOUD.dirty=false;syncPersistLS();
  try{applyTheme()}catch(e){}
  try{updateAccountUI()}catch(e){}
  try{render()}catch(e){}
  setStatus('synced');return true;
}

/* Called on login and app start. Cloud wins unless this device has unsent changes. */
export async function cloudSyncStart(){
  if(!loggedIn())return;
  try{
    setStatus('saving');
    const p=await cloudLoad();
    CLOUD.ctx=p.ctx;CLOUD.org=p.ctx.org_id;
    let owner=null;try{owner=localStorage.getItem('plume.owner')}catch(e){}
    if(!hasData(p)&&hasData(S)&&(!owner||owner===CLOUD.auth.uid)){          // fresh cloud, this person's data on this device → seed it
      clearSnapshot();await cloudPush();startPolling();return;
    }
    if(CLOUD.dirty&&hasData(S)){          // unsent local edits: diff against what the cloud has, then push
      const local=S;adoptCloud(p);
      CLOUD.muted=true;try{setState(local);rawSave(S)}finally{CLOUD.muted=false}
      await cloudPush();try{render()}catch(e){}startPolling();return;
    }
    adoptCloud(p);
    startPolling();
  }catch(e){console.error('sync start',e);CLOUD.lastErr=String(e&&e.message||e);setStatus(navigator.onLine===false?'offline':'error');startPolling()}
}

/* Cheap check: has anything in the org changed since we last saw it? */
export async function cloudCheck(force){
  if(!loggedIn()||!CLOUD.org||CLOUD.checking||CLOUD.pushing||CLOUD.dirty||document.hidden||navigator.onLine===false)return;
  if(!force&&typeof sheetStack!=='undefined'&&sheetStack.length)return;   // user is mid-form
  CLOUD.checking=true;
  try{
    const rev=await fetchRev();
    if(rev&&!sameRev(rev,CLOUD.rev)){const p=await cloudLoad();adoptCloud(p)}
    else if(CLOUD.status!=='synced'){CLOUD.lastErr='';setStatus('synced')}
  }catch(e){CLOUD.lastErr=String(e&&e.message||e);setStatus(navigator.onLine===false?'offline':'error')}
  finally{CLOUD.checking=false}
}
export function startPolling(){
  if(CLOUD.poll)return;
  CLOUD.poll=setInterval(cloudCheck,15000);
  const wake=()=>{if(!document.hidden){if(CLOUD.dirty)queueCloudSave();else cloudCheck()}};
  document.addEventListener('visibilitychange',wake);
  window.addEventListener('focus',wake);
  window.addEventListener('online',wake);
  window.addEventListener('pagehide',()=>{
    if(CLOUD.dirty&&loggedIn()&&!CLOUD.pushing){clearTimeout(CLOUD.t);cloudPush({keepalive:true}).catch(()=>{})}
  });
}
export function stopPolling(){if(CLOUD.poll){clearInterval(CLOUD.poll);CLOUD.poll=null}}
